import { getApiUrl } from "@lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface Tab {
  id: number;
  artist_id: number;
  title: string;
  body: string;
  body_rendered?: string;
  user_id: number;
  user_email?: string;
  created_at?: string;
  updated_at?: string;
  artist?: {
    id: number;
    name: string;
    slug: string;
  };
}

export interface TabListResponse {
  list: Tab[];
}

export interface TabPaginatedResponse {
  list: Tab[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface TabsQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export interface TabCreatePayload {
  artist_id: number;
  title: string;
  body: string;
  body_rendered?: string;
  user_id: number;
}

export interface TabUpdatePayload {
  artist_id?: number;
  title?: string;
  body?: string;
  body_rendered?: string;
  user_id?: number;
}

function getAuthToken(): string | null {
  return localStorage.getItem("admin_token");
}

// API functions
async function fetchTabs(params?: TabsQueryParams): Promise<TabPaginatedResponse> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }
  const apiUrl = getApiUrl();
  
  const queryParams = new URLSearchParams();
  queryParams.append("token", token);
  if (params?.page) {
    queryParams.append("page", params.page.toString());
  }
  if (params?.page_size) {
    queryParams.append("page_size", params.page_size.toString());
  }
  if (params?.search) {
    queryParams.append("search", params.search);
  }
  if (params?.sort_by) {
    queryParams.append("sort_by", params.sort_by);
  }
  if (params?.sort_order) {
    queryParams.append("sort_order", params.sort_order);
  }
  
  const response = await fetch(`${apiUrl}/tab?${queryParams.toString()}`, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error("Failed to fetch tabs");
  }
  return response.json();
}

async function fetchTab(id: number): Promise<Tab> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }
  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/tab/admin/${id}?token=${token}`, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch tab with ID ${id}`);
  }
  return response.json();
}

async function createTabApi(payload: TabCreatePayload): Promise<Tab> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }
  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/tab?token=${token}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to create tab" }));
    throw new Error(error.detail || "Failed to create tab");
  }
  return response.json();
}

async function updateTabApi({ id, payload }: { id: number; payload: TabUpdatePayload }): Promise<Tab> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }
  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/tab/admin/${id}?token=${token}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to update tab" }));
    throw new Error(error.detail || "Failed to update tab");
  }
  return response.json();
}

async function deleteTabApi(id: number): Promise<void> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }
  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/tab/admin/${id}?token=${token}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to delete tab" }));
    throw new Error(error.detail || "Failed to delete tab");
  }
}

// TanStack Query hooks
export function useTabs(params?: TabsQueryParams) {
  return useQuery<TabPaginatedResponse, Error>({
    queryKey: ["tabs", params],
    queryFn: () => fetchTabs(params),
  });
}

export function useTab(id: number) {
  return useQuery<Tab, Error>({
    queryKey: ["tab", id],
    queryFn: () => fetchTab(id),
    enabled: !!id,
  });
}

export function useCreateTab() {
  const queryClient = useQueryClient();
  return useMutation<Tab, Error, TabCreatePayload>({
    mutationFn: createTabApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tabs"] });
    },
  });
}

export function useUpdateTab() {
  const queryClient = useQueryClient();
  return useMutation<Tab, Error, { id: number; payload: TabUpdatePayload }>({
    mutationFn: updateTabApi,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["tabs"] });
      queryClient.invalidateQueries({ queryKey: ["tab", variables.id] });
    },
  });
}

export function useDeleteTab() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: deleteTabApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tabs"] });
    },
  });
}

